import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ArticleParametersPanel = ({ onGenerate, loading }) => {
  const [formData, setFormData] = useState({
    topic: '',
    style: 'news',
    length: 'medium',
    tone: 'neutral',
    sources: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  const styleOptions = [
    { value: 'news', label: 'News Report', description: 'Traditional news article format' },
    { value: 'analysis', label: 'Analysis', description: 'Analytical commentary' },
    { value: 'interview', label: 'Interview', description: 'Q&A format article' },
    { value: 'listicle', label: 'Listicle', description: 'Numbered list format article' }
  ];

  const lengthOptions = [
    { value: 'short', label: 'Short (300-500 words)', description: 'Brief article format' },
    { value: 'medium', label: 'Medium (500-800 words)', description: 'Standard article length' },
    { value: 'long', label: 'Long (800-1200 words)', description: 'Comprehensive coverage' },
    { value: 'extended', label: 'Extended (1200+ words)', description: 'In-depth investigation' }
  ];

  const toneOptions = [
    { value: 'neutral', label: 'Neutral', description: 'Objective reporting tone' },
    { value: 'formal', label: 'Formal', description: 'Professional and serious' },
    { value: 'conversational', label: 'Conversational', description: 'Accessible and engaging' },
    { value: 'optimistic', label: 'Optimistic', description: 'Positive and hopeful framing' },
    { value: 'skeptical', label: 'Skeptical', description: 'Cautious and evidence-driven' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors?.[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData?.topic?.trim()) {
      errors.topic = 'Topic is required';
    } else if (formData?.topic?.trim()?.length < 10) {
      errors.topic = 'Topic must be at least 10 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onGenerate(formData);
    }
  };

  const handleClearForm = () => {
    setFormData({
      topic: '',
      style: 'news',
      length: 'medium',
      tone: 'neutral',
      sources: ''
    });
    setValidationErrors({});
  };

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
            <Icon name="PenTool" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Article Parameters
            </h2>
            <p className="text-sm text-muted-foreground">
              Configure your article generation settings
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearForm}
          iconName="RotateCcw"
          iconSize={16}
          disabled={loading}
          className="text-muted-foreground hover:text-foreground"
        >
          Clear
        </Button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Article Topic"
          type="text"
          placeholder="Enter the main topic or headline for your article..."
          value={formData?.topic}
          onChange={(e) => handleInputChange('topic', e?.target?.value)}
          error={validationErrors?.topic}
          required
          disabled={loading}
          description="Be specific about what you want to cover"
        />

        <Select
          label="Article Style"
          options={styleOptions}
          value={formData?.style}
          onChange={(value) => handleInputChange('style', value)}
          disabled={loading}
          description="Choose the format that best fits your content"
        />

        <Select
          label="Article Length"
          options={lengthOptions}
          value={formData?.length}
          onChange={(value) => handleInputChange('length', value)}
          disabled={loading}
          description="Select the desired word count range"
        />

        <Select
          label="Writing Tone"
          options={toneOptions}
          value={formData?.tone}
          onChange={(value) => handleInputChange('tone', value)}
          disabled={loading}
          description="Set the overall tone and voice"
        />

        <Input
          label="Source References (Optional)"
          type="text"
          placeholder="Add any specific sources, quotes, or references..."
          value={formData?.sources}
          onChange={(e) => handleInputChange('sources', e?.target?.value)}
          disabled={loading}
          description="Include any specific information to reference"
        />

        <div className="pt-4 border-t border-border">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={loading}
            disabled={loading || !formData?.topic?.trim()}
            className="bg-gradient-primary hover:opacity-90 transition-opacity duration-250"
            iconName="Sparkles"
            iconPosition="left"
            iconSize={18}
          >
            {loading ? 'Generating Article...' : 'Generate Article'}
          </Button>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
            <span>Generation typically takes 30-60 seconds</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={12} color="var(--color-success)" />
            <span>All content is AI-generated and should be fact-checked</span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ArticleParametersPanel;